/*
 * Bulk Orders page.
 *
 * Two custom elements:
 *
 * `bulk-order-page`      owns the two toggle rows and the running cart bar. It is also
 *                        the ONLY thing that listens for cart changes coming from
 *                        elsewhere on the page (mini cart, upsell). The stock
 *                        `bulk-order-list` subscribes per list, which here would mean
 *                        fifteen identical section fetches for one cart change.
 *
 * `bulk-order-page-list` one product's variant table. Forked from `bulk-order-list.js`
 *                        because that class re-renders by grabbing the first `table` and
 *                        the first `.bulk-order-list-total` out of the re-rendered
 *                        section, which is correct when a section holds one list and
 *                        wrong when it holds fifteen. Everything here is scoped to
 *                        `#<this.id>` in the response.
 *
 * Depends on BulkAdd, debounce, subscribe, publish, PUB_SUB_EVENTS, fetchConfig and
 * routes, all globals from `scripts.js`.
 */

if (!customElements.get("bulk-order-page-list")) {
  customElements.define(
    "bulk-order-page-list",
    class BulkOrderPageList extends BulkAdd {
      hasPendingQuantityUpdate = false;

      constructor() {
        super();
      }

      connectedCallback() {
        this.initEventListeners();
      }

      get page() {
        return this.closest("bulk-order-page");
      }

      get bulkOrderListTable() {
        return this.querySelector("table");
      }

      get cartVariantsForProduct() {
        return JSON.parse(this.querySelector("[data-cart-contents]")?.innerHTML || "[]");
      }

      get removeAllButton() {
        return this.querySelector("button.bulk-order-remove-product-from-cart");
      }

      get totalBar() {
        return this.querySelector(".bulk-order-list-total");
      }

      initEventListeners() {
        this.bulkOrderListTable?.addEventListener(
          "keydown",
          this.handleSwitchVariantOnEnter.bind(this),
        );

        this.initVariantEventListeners();
      }

      initVariantEventListeners() {
        this.allInputsArray = Array.from(this.querySelectorAll('input[type="number"]'));

        this.querySelectorAll("quantity-input").forEach((qty) => {
          const debouncedOnChange = debounce(
            this.onChange.bind(this),
            BulkAdd.ASYNC_REQUEST_DELAY,
            false,
          );
          qty.addEventListener("change", (event) => {
            this.hasPendingQuantityUpdate = true;
            debouncedOnChange(event);
          });
        });

        this.querySelectorAll(".bulk-order-list-remove-button").forEach((button) => {
          button.addEventListener("click", (event) => {
            event.preventDefault();
            this.startQueue(button.dataset.index, 0);
          });
        });

        this.removeAllButton?.addEventListener("click", this.handleRemoveAll.bind(this));
      }

      handleRemoveAll(event) {
        event.preventDefault();
        const items = this.cartVariantsForProduct.reduce(
          (acc, variantId) => ({ ...acc, [variantId]: 0 }),
          {},
        );

        if (Object.keys(items).length === 0) return;

        this.updateMultipleQty(items);
      }

      onChange(event) {
        const inputValue = parseInt(event.target.value);

        if (inputValue == 0) {
          event.target.setAttribute("value", inputValue);
          this.startQueue(event.target.dataset.index, inputValue);
        } else {
          this.validateQuantity(event);
        }
      }

      setLoading(loading = false, element) {
        const progressBar = element?.querySelector(".qty-progress-bar-container");
        const spinner = element?.querySelector(".spinner");
        const checkmark = element?.querySelector(".quantity-success-check");

        if (!progressBar || !spinner || !checkmark) return;

        if (loading) {
          progressBar.classList.add("visible");
          spinner.classList.add("visible");
          checkmark.classList.remove("visible");
        } else {
          progressBar.classList.remove("visible");
          spinner.classList.remove("visible");
          checkmark.classList.add("visible");
          setTimeout(() => checkmark.classList.remove("visible"), 1250);
        }
      }

      validateInput(target) {
        const targetValue = parseInt(target.value);
        const targetMin = parseInt(target.dataset.min);
        const targetStep = parseInt(target.step);

        if (target.max) {
          return (
            targetValue == 0 ||
            (targetValue >= targetMin &&
              targetValue <= parseInt(target.max) &&
              targetValue % targetStep == 0)
          );
        }

        return targetValue == 0 || (targetValue >= targetMin && targetValue % targetStep == 0);
      }

      handleSwitchVariantOnEnter(event) {
        if (event.key !== "Enter" || event.target.tagName !== "INPUT") {
          return;
        }

        event.preventDefault();
        event.target.blur();

        if (!this.validateInput(event.target) || this.allInputsArray.length <= 1) {
          return;
        }

        const currentIndex = this.allInputsArray.indexOf(event.target);
        const offset = event.shiftKey ? -1 : 1;
        const nextIndex =
          (currentIndex + offset + this.allInputsArray.length) % this.allInputsArray.length;

        this.allInputsArray[nextIndex]?.select();
        this.allInputsArray[nextIndex]?.scrollIntoView({
          block: "nearest",
          behavior: "instant",
        });
      }

      getSectionsToRender() {
        return [
          {
            id: this.id,
            section: this.dataset.section,
          },
          {
            id: "header",
            section: document.getElementById("header")?.dataset.id,
            selector: ".head-slot-cart-link",
          },
          {
            id: "header-mini-cart-content",
            section: "header-mini-cart-content",
          },
          {
            id: "header-mini-cart-footer",
            section: "header-mini-cart-footer",
          },
          {
            id: "mobile-dock",
            section: document.getElementById("mobile-dock")?.dataset.id,
            selector: ".head-slot-cart-link",
          },
        ].filter((section) => {
          const element = document.getElementById(section.id);
          return element && (section.selector ? element.querySelector(section.selector) : true);
        });
      }

      getSectionElement(html, selector = ".shopify-section") {
        return new DOMParser().parseFromString(html, "text/html").querySelector(selector);
      }

      renderSections(parsedState) {
        const { sections } = parsedState;

        this.getSectionsToRender().forEach(({ id, selector, section }) => {
          const sectionElement = document.getElementById(id);
          if (!sectionElement || !sections[section]) {
            return;
          }

          if (section === this.dataset.section) {
            if (this.queue.length > 0 || this.hasPendingQuantityUpdate) {
              return;
            }

            // Scope to THIS list inside the re-rendered section. The section holds one
            // list per product, so an unscoped `querySelector("table")` would paste the
            // first product's rows into whichever list the shopper is actually using.
            const newSection = this.getSectionElement(sections[section]);
            const newList = newSection?.querySelector(`#${CSS.escape(this.id)}`);
            if (!newList) return;

            const focusedElement = document.activeElement;
            let focusTarget = focusedElement?.dataset?.target;
            if (focusTarget?.includes("remove")) {
              focusTarget = focusedElement
                .closest("quantity-input")
                ?.querySelector('[data-target*="increment-"]')?.dataset.target;
            }

            const newTotal = newList.querySelector(".bulk-order-list-total");
            if (this.totalBar && newTotal) {
              this.totalBar.innerHTML = newTotal.innerHTML;
            }

            const newTable = newList.querySelector("table");
            if (this.bulkOrderListTable && newTable) {
              this.bulkOrderListTable.innerHTML = newTable.innerHTML;

              const newFocusTarget = this.querySelector(`[data-target='${focusTarget}']`);
              newFocusTarget?.focus({ preventScroll: true });

              this.initVariantEventListeners();
            }

            this.page?.updateCartBarFrom(newSection);
          } else {
            const elementToReplace = sectionElement.querySelector(selector) ?? sectionElement;
            const newSection = this.getSectionElement(sections[section], selector);
            if (!elementToReplace || !newSection) {
              return;
            }
            elementToReplace.innerHTML = newSection.innerHTML;
          }
        });
      }

      updateMultipleQty(items) {
        for (const index of Object.keys(items)) {
          const qtyInput = document.getElementById(`Quantity-${index}`)?.closest("quantity-input");
          this.setLoading(true, qtyInput);
        }

        if (this.queue.length == 0) {
          this.hasPendingQuantityUpdate = false;
        }

        const url = this.dataset.url || window.location.pathname;

        const body = JSON.stringify({
          updates: items,
          sections: this.getSectionsToRender().map(({ section }) => section),
          sections_url: url,
        });

        fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
          .then((response) => response.text())
          .then(async (state) => {
            const parsedState = JSON.parse(state);
            this.renderSections(parsedState);
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: this.page?.id || this.id,
              cartData: parsedState,
            });
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            for (const index of Object.keys(items)) {
              const qtyInput = document
                .getElementById(`Quantity-${index}`)
                ?.closest("quantity-input");
              this.setLoading(false, qtyInput);
            }

            this.setRequestStarted(false);
          });
      }
    },
  );
}

if (!customElements.get("bulk-order-page")) {
  customElements.define(
    "bulk-order-page",
    class BulkOrderPage extends HTMLElement {
      cartUpdateUnsubscriber = undefined;

      connectedCallback() {
        this.initToggles();

        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
          // Quantity changes made inside this section already re-rendered themselves.
          if (event.source === this.id) return;

          await this.refresh();
        });
      }

      disconnectedCallback() {
        this.cartUpdateUnsubscriber?.();
      }

      get groupTabs() {
        return Array.from(this.querySelectorAll("[data-group-tab]"));
      }

      get panelTabs() {
        return Array.from(this.querySelectorAll("[data-panel-tab]"));
      }

      get panels() {
        return Array.from(this.querySelectorAll(".bulk-order-page-panel"));
      }

      initToggles() {
        this.groupTabs.forEach((tab) => {
          tab.addEventListener("click", () => this.showGroup(tab.dataset.groupTab));
        });

        this.panelTabs.forEach((tab) => {
          tab.addEventListener("click", () => this.showPanel(tab.dataset.panelTab));
        });
      }

      showGroup(groupId) {
        this.groupTabs.forEach((tab) => {
          const isActive = tab.dataset.groupTab === groupId;
          tab.classList.toggle("is-active", isActive);
          tab.setAttribute("aria-pressed", isActive ? "true" : "false");
        });

        this.querySelectorAll("[data-group-panel]").forEach((row) => {
          row.hidden = row.dataset.groupPanel !== groupId;
        });

        // Re-open whichever size was last active in this group, or its first one.
        const activeRow = this.querySelector(`[data-group-panel="${groupId}"]`);
        const target =
          activeRow?.querySelector("[data-panel-tab].is-active") ??
          activeRow?.querySelector("[data-panel-tab]");

        if (target) {
          this.showPanel(target.dataset.panelTab);
        }
      }

      showPanel(panelId) {
        this.panelTabs.forEach((tab) => {
          if (tab.dataset.panelTab === panelId) {
            tab.classList.add("is-active");
            tab.setAttribute("aria-pressed", "true");
          } else if (tab.closest("[data-group-panel]") === this.activeGroupRow) {
            tab.classList.remove("is-active");
            tab.setAttribute("aria-pressed", "false");
          }
        });

        this.panels.forEach((panel) => {
          panel.hidden = panel.dataset.panel !== panelId;
        });
      }

      get activeGroupRow() {
        return this.querySelector("[data-group-panel]:not([hidden])");
      }

      updateCartBarFrom(newSection) {
        const bar = this.querySelector(".bulk-order-page-cart-bar");
        const newBar = newSection?.querySelector(".bulk-order-page-cart-bar");
        if (!bar || !newBar) return;

        bar.innerHTML = newBar.innerHTML;
        bar.classList.toggle("is-filled", newBar.classList.contains("is-filled"));
      }

      /*
       * A cart change from somewhere else on the page. One fetch for the whole section,
       * then each list swaps in its own re-rendered body. Toggle state survives because
       * it lives on the panel wrappers, which are never replaced.
       */
      async refresh() {
        const url = this.dataset.url || window.location.pathname;

        try {
          const response = await fetch(`${url}?section_id=${this.dataset.section}`);
          const responseText = await response.text();
          const newSection = new DOMParser().parseFromString(responseText, "text/html");

          this.querySelectorAll("bulk-order-page-list").forEach((list) => {
            const newList = newSection.querySelector(`#${CSS.escape(list.id)}`);
            if (!newList) return;

            list.innerHTML = newList.innerHTML;
            list.initEventListeners();
          });

          this.updateCartBarFrom(newSection);
        } catch (e) {
          console.error(e);
        }
      }
    },
  );
}
